import type { Vec2 } from '../math';
import { clonePalette, coercePalette, type PaletteSpec } from '../palette';
import type { Curve } from './curve';

/**
 * The declarative parameter system.
 *
 * This is the load-bearing abstraction of the whole app. A technique describes its
 * parameters once, and three consumers read that same description:
 *
 *   1. the UI, which generates every control automatically — no control is ever
 *      written by hand for a technique;
 *   2. the randomiser, which knows the valid range and the distribution;
 *   3. the batch engine, which sweeps or samples the parameter space.
 *
 * Because all three read one source, a new parameter is a single line of code and
 * immediately works everywhere.
 */

export interface ParamBase {
  key: string;
  label: string;
  /** Collapsible group in the panel, e.g. "Bewegung", "Darstellung", "Farbe". */
  group?: string;
  /** One sentence explaining what the parameter does. Shown on hover. */
  tooltip?: string;
  /** Hides the control when it is irrelevant for the current settings. */
  showIf?: (values: ParamValues) => boolean;
  /** Excluded from randomisation and batch variation regardless of user locks. */
  neverRandomise?: boolean;
}

export interface NumericParamBase extends ParamBase {
  min: number;
  max: number;
  step: number;
  default: number;
  unit?: string;
  /** Log scale keeps ranges like 100 … 200 000 usable over the whole track. */
  scale?: 'linear' | 'log';
  /**
   * Default bounds for randomisation and batch variation, when the full range would
   * mostly produce ugly results. Falls back to [min, max].
   */
  bulkRange?: [number, number];
  /** Decimal places in the number field; derived from `step` when omitted. */
  precision?: number;
}

export interface FloatParam extends NumericParamBase {
  kind: 'float';
}

export interface IntParam extends NumericParamBase {
  kind: 'int';
}

export interface BoolParam extends ParamBase {
  kind: 'bool';
  default: boolean;
  /** Probability of `true` when randomised. Defaults to 0.5. */
  bulkProbability?: number;
}

export interface EnumOption<T extends string = string> {
  value: T;
  label: string;
  /** Optional short glyph shown in segmented controls. */
  icon?: string;
}

export interface EnumParam<T extends string = string> extends ParamBase {
  kind: 'enum';
  options: readonly EnumOption<T>[];
  default: T;
  /** Subset allowed during randomisation and batch runs. Defaults to all options. */
  bulkValues?: readonly T[];
  /** Render as a segmented control instead of a dropdown. Best up to four options. */
  segmented?: boolean;
}

export interface ColorParam extends ParamBase {
  kind: 'color';
  default: string;
  alpha?: boolean;
}

export interface PaletteParam extends ParamBase {
  kind: 'palette';
  default: PaletteSpec;
}

export interface Vec2Param extends ParamBase {
  kind: 'vec2';
  min: Vec2;
  max: Vec2;
  step: number;
  default: Vec2;
  unit?: string;
  /** Draws the pad with a 1:1 aspect ratio; off for ranges with very different spans. */
  square?: boolean;
}

export interface CurveParam extends ParamBase {
  kind: 'curve';
  default: Curve;
  /** Labels for the two axes in the editor, e.g. "Geschwindigkeit" → "Breite". */
  xLabel?: string;
  yLabel?: string;
}

export interface RangeParam extends ParamBase {
  kind: 'range';
  min: number;
  max: number;
  step: number;
  default: [number, number];
  unit?: string;
  scale?: 'linear' | 'log';
  precision?: number;
}

export interface TextParam extends ParamBase {
  kind: 'text';
  default: string;
  multiline?: boolean;
  placeholder?: string;
  /** Values the batch engine may draw from. */
  bulkValues?: readonly string[];
}

export interface SeedParam extends ParamBase {
  kind: 'seed';
  default: number;
}

export type Param =
  | FloatParam
  | IntParam
  | BoolParam
  | EnumParam
  | ColorParam
  | PaletteParam
  | Vec2Param
  | CurveParam
  | RangeParam
  | TextParam
  | SeedParam;

export type ParamSchema = readonly Param[];

/** Every value a parameter can hold. */
export type ParamValue = number | boolean | string | Vec2 | Curve | [number, number] | PaletteSpec;

export type ParamValues = Record<string, ParamValue>;

// ── Type-level mapping from a schema to its value object ─────────────────────
//
// Declaring a schema with `as const satisfies ParamSchema` makes `SchemaValues<>`
// resolve to a precise object type, so technique code reads `p.particleCount` as a
// number and `p.mode` as its literal union — not as `ParamValue`.

type ValueOfParam<P> = P extends { kind: 'float' | 'int' | 'seed' }
  ? number
  : P extends { kind: 'bool' }
    ? boolean
    : P extends { kind: 'enum'; options: readonly EnumOption<infer T>[] }
      ? T
      : P extends { kind: 'color' | 'text' }
        ? string
        : P extends { kind: 'palette' }
          ? PaletteSpec
          : P extends { kind: 'vec2' }
          ? Vec2
          : P extends { kind: 'curve' }
            ? Curve
            : P extends { kind: 'range' }
              ? [number, number]
              : never;

export type SchemaValues<S extends ParamSchema> = {
  [P in S[number] as P['key']]: ValueOfParam<P>;
};

// ── Runtime helpers ──────────────────────────────────────────────────────────

export function isNumericParam(p: Param): p is FloatParam | IntParam {
  return p.kind === 'float' || p.kind === 'int';
}

/** The bounds randomisation and batch runs should stay within by default. */
export function paramBulkRange(p: FloatParam | IntParam): [number, number] {
  return p.bulkRange ?? [p.min, p.max];
}

export function defaultValues(schema: ParamSchema): ParamValues {
  const values: ParamValues = {};
  for (const p of schema) {
    switch (p.kind) {
      case 'curve':
        values[p.key] = p.default.map((pt) => ({ x: pt.x, y: pt.y }));
        break;
      case 'vec2':
        values[p.key] = { x: p.default.x, y: p.default.y };
        break;
      case 'palette':
        values[p.key] = clonePalette(p.default);
        break;
      case 'range':
        values[p.key] = [p.default[0], p.default[1]];
        break;
      default:
        values[p.key] = p.default;
    }
  }
  return values;
}

/** Fills in any parameter the stored values are missing — presets survive schema growth. */
export function withDefaults(schema: ParamSchema, values: ParamValues): ParamValues {
  const out = defaultValues(schema);
  for (const p of schema) {
    if (Object.prototype.hasOwnProperty.call(values, p.key)) out[p.key] = values[p.key];
  }
  return out;
}

export function findParam(schema: ParamSchema, key: string): Param | undefined {
  return schema.find((p) => p.key === key);
}

/** Groups in declaration order, so panels keep a deliberate reading order. */
export function schemaGroups(schema: ParamSchema): { name: string; params: Param[] }[] {
  const groups: { name: string; params: Param[] }[] = [];
  const byName = new Map<string, { name: string; params: Param[] }>();
  for (const p of schema) {
    const name = p.group ?? 'Allgemein';
    let group = byName.get(name);
    if (!group) {
      group = { name, params: [] };
      byName.set(name, group);
      groups.push(group);
    }
    group.params.push(p);
  }
  return groups;
}

export function isVisible(p: Param, values: ParamValues): boolean {
  return p.showIf ? p.showIf(values) : true;
}

/** Clamps a value back into what the parameter allows — used when loading presets. */
export function coerceValue(p: Param, value: ParamValue | undefined): ParamValue {
  if (value === undefined) return defaultValues([p])[p.key];
  switch (p.kind) {
    case 'float':
    case 'int': {
      const n = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(n)) return p.default;
      const clamped = Math.min(p.max, Math.max(p.min, n));
      return p.kind === 'int' ? Math.round(clamped) : clamped;
    }
    case 'seed': {
      const n = typeof value === 'number' ? value : Number(value);
      return Number.isFinite(n) ? Math.floor(Math.abs(n)) : p.default;
    }
    case 'bool':
      return typeof value === 'boolean' ? value : p.default;
    case 'enum':
      return p.options.some((o) => o.value === value) ? (value as string) : p.default;
    case 'range': {
      if (!Array.isArray(value) || value.length !== 2) return [p.default[0], p.default[1]];
      const lo = Math.min(p.max, Math.max(p.min, Number(value[0])));
      const hi = Math.min(p.max, Math.max(p.min, Number(value[1])));
      return lo <= hi ? [lo, hi] : [hi, lo];
    }
    case 'vec2': {
      const v = value as Vec2;
      if (typeof v?.x !== 'number' || typeof v?.y !== 'number') return { ...p.default };
      return {
        x: Math.min(p.max.x, Math.max(p.min.x, v.x)),
        y: Math.min(p.max.y, Math.max(p.min.y, v.y)),
      };
    }
    case 'curve':
      return Array.isArray(value) && value.length >= 2 ? (value as Curve) : p.default.map((pt) => ({ ...pt }));
    case 'palette':
      return coercePalette(value);
    default:
      return typeof value === 'string' ? value : p.default;
  }
}
